import React, { useEffect, useState } from "react";

import { copy } from "shared/assets";
import { PATHS } from "App/router";
import { Spacer } from "shared/layout";
import { Close, Hamburger, Logo } from "shared/assets/icons";
import {
  StyledNavbar,
  NavMainLink,
  LeftContent,
  UserInitials,
  CurrentUserContent,
  StyledNavLink,
  MobileMenu,
  MobileCloseButton,
} from "./styles";
import { IUser } from "Auth/types";
import { useBreakpoint, isBandsDomain } from "shared/utils";
import { BreakpointSize, slideInFromRight } from "shared/styles/constants";
import { IconButton } from "shared/components";
import { BAND_PATHS } from "Bands/router";

export interface NavbarProps {
  title?: string;
  currentUser?: IUser;
  navItems: INavItem[];
  profilePagePath: string;
  homePagePath?: string;
  mainNavItem?: JSX.Element;
  currentPath?: string;
}

interface INavItem {
  to: string;
  label: string;
  dataTestId?: string;
}

export const Navbar: React.FC<NavbarProps> = ({
  currentUser,
  title,
  navItems,
  profilePagePath,
  homePagePath,
  mainNavItem,
  currentPath,
}) => {
  const { firstname, lastname } = currentUser?.attributes || {};
  const isMobile = useBreakpoint(BreakpointSize.m);
  const [isMobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setMobileOpen(false);
    }, 100);
  }, [currentPath]);

  const toggleMobileMenu = () => {
    setMobileOpen(!isMobileOpen);
  };

  const renderLeftContent = () => (
    <>
      {navItems.map(({ to, label, dataTestId }, index) => (
        <StyledNavLink to={to} key={index} data-testid={dataTestId}>
          {label}
        </StyledNavLink>
      ))}
    </>
  );

  const renderRightContent = () => (
    <Spacer size="xs" spaceDirection={isMobile ? "vertical" : "horizontal"}>
      {currentUser ? (
        <Spacer size="m" spaceDirection={isMobile ? "vertical" : "horizontal"}>
          <CurrentUserContent to={profilePagePath}>
            <UserInitials>
              <span>
                {firstname?.charAt(0)}
                {lastname?.charAt(0)}
              </span>
            </UserInitials>
            <span>{firstname}</span>
          </CurrentUserContent>
          {isBandsDomain && (
            <StyledNavLink to={BAND_PATHS.MY_VENUES}>{copy.venueOverview.title}</StyledNavLink>
          )}
        </Spacer>
      ) : (
        <>
          <StyledNavLink to={PATHS.REGISTER}>{copy.auth.register}</StyledNavLink>
          <StyledNavLink to={PATHS.LOGIN}>{copy.auth.login}</StyledNavLink>
        </>
      )}
    </Spacer>
  );

  return (
    <StyledNavbar aria-label={copy.a11y.mainNav}>
      <LeftContent>
        {mainNavItem || (
          <NavMainLink
            {...(homePagePath ? { to: currentUser ? homePagePath : PATHS.LOGIN } : { as: "span" })}
          >
            <Logo />
            <h1>{title}</h1>
          </NavMainLink>
        )}
        {!isMobile && renderLeftContent()}
      </LeftContent>
      {!isMobile && renderRightContent()}

      {isMobile && (
        <Spacer size="s" justifyContent="center">
          <IconButton
            size="l"
            label="Menu"
            onClick={toggleMobileMenu}
            icon={isMobileOpen ? Close : Hamburger}
          />
        </Spacer>
      )}

      {isMobile && isMobileOpen && (
        <MobileMenu {...slideInFromRight}>
          <MobileCloseButton>
            <IconButton size="l" label="Menu" onClick={toggleMobileMenu} icon={Close} />
          </MobileCloseButton>
          {renderLeftContent()}
          {renderRightContent()}
        </MobileMenu>
      )}
    </StyledNavbar>
  );
};
